/**
 * Voice & audio (prototype Settings > Voice & video): devices, input mode,
 * push-to-talk key, release delay, input sensitivity with a live meter that
 * runs only while you test, the keybinds (Toggle mute, Toggle deafen) and the
 * camera ("Coming soon" while video is off).
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useT } from '../i18n';
import { applyDevices, hotkeysMode, pttMode, setupHotkeys, setupInput } from '../voice/controller';
import { reservedCombo } from '../voice/hotkeys';
import { MicOpenError, openMicStream, type MicFailure } from '../voice/mic';
import { useVoice, type InputMode } from '../voice/store';
import { Btn, Icon, Range, Seg, Select } from '../ui/kit';
import { ComboCapture, KeyCapture } from './KeyCapture';
import { SetGroup, SetRow } from './Settings';
import { useMediaFlags, VideoView } from '../hub/Streams';
import { currentCaps } from '../voice/live';
import { cameraSupported, openCamera, publishCapsOf } from '../voice/media';
import { micConstraints, processMic, type ProcessedMic } from '../voice/noise';
import { NoiseSuppressionGroup, PriorityKeyRow, testNoiseMode } from '../voice/VoiceExtras';
import { useFlag } from '../store/config';
import { canPickOutput as outputPickable, useDevices } from '../voice/devices';

function Meter() {
  const { t } = useT();
  const sensitivity = useVoice((v) => v.prefs.sensitivity);
  const inputId = useVoice((v) => v.prefs.inputDeviceId);
  // the test hears what voice would send: the chosen noise suppression (2026-11-01)
  const noise = useVoice(() => testNoiseMode());
  const [on, setOn] = useState(false);
  const [level, setLevel] = useState(0);
  const [err, setErr] = useState<MicFailure | null>(null);
  const [fellBack, setFellBack] = useState(false);
  const raf = useRef(0);
  useEffect(() => {
    if (!on) return;
    let stream: MediaStream | null = null;
    let proc: ProcessedMic | null = null;
    let ctx: AudioContext | null = null;
    let live = true;
    // the same microphone voice opens: the chosen one, or the default when it is gone
    void openMicStream(inputId, micConstraints(noise))
      .then(async ({ stream: s, fellBack: fb }) => {
        const raw = s.getAudioTracks()[0];
        const p = raw ? await processMic(raw, noise) : null;
        if (!live) {
          p?.stop();
          s.getTracks().forEach((x) => x.stop());
          return;
        }
        setFellBack(fb);
        stream = s;
        proc = p;
        ctx = new AudioContext();
        const an = ctx.createAnalyser();
        an.fftSize = 2048;
        // Strong's processed track, or the stream as opened
        ctx
          .createMediaStreamSource(p && p.track !== raw ? new MediaStream([p.track]) : s)
          .connect(an);
        const buf = new Float32Array(an.fftSize);
        const tick = () => {
          an.getFloatTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += (buf[i] ?? 0) ** 2;
          setLevel(Math.min(1, Math.sqrt(sum / buf.length) * 2));
          raf.current = requestAnimationFrame(tick);
        };
        tick();
      })
      .catch((e: unknown) => {
        if (!live) return;
        setErr(e instanceof MicOpenError ? e.failure : 'failed');
        setOn(false);
      });
    return () => {
      live = false;
      cancelAnimationFrame(raf.current);
      proc?.stop();
      stream?.getTracks().forEach((x) => x.stop());
      void ctx?.close();
      setLevel(0);
    };
  }, [on, inputId, noise]);
  const heard = level >= sensitivity;
  return (
    <div className="ns-meter-wrap">
      <div
        className={'ns-meter' + (heard ? ' heard' : '')}
        role="meter"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(level * 100)}
        aria-label={t('ns.voice.meter')}
      >
        <i style={{ '--lv': level } as CSSProperties} />
        <b style={{ '--th': sensitivity } as CSSProperties} aria-hidden="true" />
      </div>
      <Btn
        size="sm"
        icon={on ? 'x' : 'mic'}
        onClick={() => {
          if (!on) {
            setErr(null);
            setFellBack(false);
          }
          setOn(!on);
        }}
      >
        {on ? t('ns.voice.stopTest') : t('ns.voice.test')}
      </Btn>
      {on && fellBack ? <p className="ns-help">{t('set.voice.micMissing')}</p> : null}
      {err ? (
        <p className="ns-err" role="alert">
          {err === 'denied'
            ? t('ns.voice.noMic')
            : err === 'none'
              ? t('set.voice.noMicFound')
              : t('set.voice.micFailed')}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The camera for video in voice (2026-10-29-screen-share-and-video.md):
 * "Coming soon" while the `video` flag is off for you; else the camera to
 * use and a test preview that runs only while you look at it.
 */
type KeybindPref = 'muteKey' | 'deafenKey';

/**
 * Keybinds (Discord's): Toggle mute, Toggle deafen and the priority
 * speaker's key (held), under push-to-talk's key. Each records a key or a
 * combination or is cleared; one another keybind (or push-to-talk) uses, or
 * one the browser or system keeps, says so.
 */
function KeybindsGroup() {
  const { t } = useT();
  const prefs = useVoice((v) => v.prefs);
  const setPrefs = useVoice((v) => v.setPrefs);
  const global = hotkeysMode() === 'desktop';
  const prioOn = useFlag('priority_speaker');
  const rows: Array<{ pref: KeybindPref; label: string }> = [
    { pref: 'muteKey', label: t('ns.key.toggleMute') },
    { pref: 'deafenKey', label: t('ns.key.toggleDeafen') },
  ];
  const warning = (pref: KeybindPref): string | undefined => {
    const v = prefs[pref];
    if (!v) return undefined;
    const other = pref === 'muteKey' ? prefs.deafenKey : prefs.muteKey;
    if (
      v === other ||
      (prefs.inputMode === 'ptt' && v === prefs.pttKey) ||
      (prioOn && v === prefs.priorityKey)
    )
      return t('ns.key.conflict');
    return reservedCombo(v, global) ? t('ns.key.reserved') : undefined;
  };
  return (
    <SetGroup title={t('ns.key.keybinds')}>
      {rows.map(({ pref, label }) => (
        <SetRow
          key={pref}
          label={label}
          sub={warning(pref) ?? t(global ? 'ns.key.desktopNote' : 'ns.key.webNote')}
        >
          <ComboCapture
            value={prefs[pref]}
            label={label}
            onChange={(combo) => {
              setPrefs({ [pref]: combo });
              setupHotkeys();
            }}
          />
        </SetRow>
      ))}
      {/* priority speaker (2026-11-01): a key held, next to mute and deafen */}
      <PriorityKeyRow
        conflict={
          !!prefs.priorityKey &&
          ((prefs.inputMode === 'ptt' && prefs.priorityKey === prefs.pttKey) ||
            prefs.priorityKey === prefs.muteKey ||
            prefs.priorityKey === prefs.deafenKey)
        }
      />
    </SetGroup>
  );
}

function CameraGroup({ cameras }: { cameras: MediaDeviceInfo[] }) {
  const { t } = useT();
  const flags = useMediaFlags();
  const id = useVoice((v) => v.prefs.cameraDeviceId);
  const setPrefs = useVoice((v) => v.setPrefs);
  const [test, setTest] = useState<MediaStream | null>(null);
  const [err, setErr] = useState(false);
  // the test preview ends with the page, a device change or the flag going off
  useEffect(
    () => () => {
      for (const tr of test?.getTracks() ?? []) tr.stop();
    },
    [test],
  );
  if (!flags.camera || !cameraSupported())
    return (
      <SetGroup title={t('stream.set.camera')}>
        <div className="nx-set-note" data-camera="soon">
          <Icon name="video" />
          <span>{t('stream.set.soon')}</span>
        </div>
      </SetGroup>
    );
  const start = () => {
    setErr(false);
    openCamera(publishCapsOf('camera', currentCaps()), id)
      .then((track) => setTest(new MediaStream([track])))
      .catch(() => setErr(true));
  };
  return (
    <SetGroup title={t('stream.set.camera')}>
      <SetRow label={t('stream.set.camera')} sub={t('stream.set.cameraHint')}>
        <Select
          className="nx-input ns-select"
          value={id ?? ''}
          onChange={(e) => {
            setPrefs({ cameraDeviceId: e.target.value || null });
            setTest(null);
          }}
        >
          <option value="">{t('stream.set.default')}</option>
          {cameras.map((d, i) => (
            <option key={d.deviceId || i} value={d.deviceId}>
              {d.label || `${t('stream.set.camera')} ${i + 1}`}
            </option>
          ))}
        </Select>
      </SetRow>
      {test ? (
        <div className="ns-cam-test">
          <VideoView stream={test} fit="cover" mirror />
        </div>
      ) : null}
      <div className="nx-set-note">
        <Btn size="sm" icon="video" onClick={() => (test ? setTest(null) : start())}>
          {test ? t('stream.set.stopTest') : t('stream.set.test')}
        </Btn>
        {err ? <span role="alert">{t('stream.err.camera')}</span> : null}
      </div>
    </SetGroup>
  );
}

export function VoiceSection() {
  const { t } = useT();
  const prefs = useVoice((v) => v.prefs);
  const setPrefs = useVoice((v) => v.setPrefs);
  const { list, refresh } = useDevices();
  const inputs = list.filter((d) => d.kind === 'audioinput');
  const outputs = list.filter((d) => d.kind === 'audiooutput');
  const unlabeled = inputs.length > 0 && inputs.every((d) => !d.label);
  // the saved microphone is not connected now: say so, instead of showing "System default"
  // (ST-B05, HV-B07)
  const inputMissing =
    !!prefs.inputDeviceId &&
    inputs.length > 0 &&
    !unlabeled &&
    !inputs.some((d) => d.deviceId === prefs.inputDeviceId);
  const canPickOutput = outputPickable();
  const web = pttMode() === 'focused';
  return (
    <>
      <h2>{t('ns.set.voice')}</h2>
      <SetGroup title={t('ns.voice.devices')}>
        <SetRow
          label={t('ns.voice.input')}
          sub={inputMissing ? t('set.voice.micMissing') : undefined}
        >
          <Select
            className="nx-input ns-select"
            value={prefs.inputDeviceId ?? ''}
            data-missing={inputMissing || undefined}
            onChange={(e) => {
              setPrefs({ inputDeviceId: e.target.value || null });
              void applyDevices();
            }}
          >
            <option value="">{t('ns.voice.default')}</option>
            {inputMissing ? (
              <option value={prefs.inputDeviceId ?? ''} disabled>
                {t('set.voice.missingOption')}
              </option>
            ) : null}
            {inputs.map((d, i) => (
              <option key={d.deviceId || i} value={d.deviceId}>
                {d.label || t('ns.voice.micN', { n: i + 1 })}
              </option>
            ))}
          </Select>
        </SetRow>
        <SetRow
          label={t('ns.voice.output')}
          sub={canPickOutput ? undefined : t('ns.voice.outputNo')}
        >
          <Select
            className="nx-input ns-select"
            value={prefs.outputDeviceId ?? ''}
            disabled={!canPickOutput}
            onChange={(e) => {
              setPrefs({ outputDeviceId: e.target.value || null });
              void applyDevices();
            }}
          >
            <option value="">{t('ns.voice.default')}</option>
            {outputs.map((d, i) => (
              <option key={d.deviceId || i} value={d.deviceId}>
                {d.label || t('ns.voice.spkN', { n: i + 1 })}
              </option>
            ))}
          </Select>
        </SetRow>
        {unlabeled ? (
          <div className="nx-set-note">
            <Icon name="mic" />
            <Btn
              size="sm"
              icon="mic"
              onClick={() =>
                void navigator.mediaDevices
                  .getUserMedia({ audio: true })
                  .then((s) => {
                    s.getTracks().forEach((x) => x.stop());
                    refresh();
                  })
                  .catch(() => {})
              }
            >
              {t('ns.voice.showNames')}
            </Btn>
          </div>
        ) : null}
      </SetGroup>
      <SetGroup title={t('ns.voice.inputMode')}>
        <SetRow label={t('ns.voice.inputMode')}>
          <Seg<InputMode>
            label={t('ns.voice.inputMode')}
            value={prefs.inputMode}
            onChange={(v) => {
              setPrefs({ inputMode: v });
              setupInput();
            }}
            options={[
              { value: 'va', label: t('ns.voice.va'), icon: 'wave' },
              { value: 'ptt', label: t('ns.voice.ptt'), icon: 'cm-key' },
            ]}
          />
        </SetRow>
        {prefs.inputMode === 'ptt' ? (
          <>
            <SetRow
              label={t('ns.voice.pttKey')}
              sub={web ? t('ns.voice.pttWebNote') : t('ns.voice.pttDesktopNote')}
            >
              <KeyCapture
                value={prefs.pttKey}
                label={t('ns.voice.pttKey')}
                onChange={(code) => {
                  setPrefs({ pttKey: code });
                  setupInput();
                }}
              />
            </SetRow>
            <SetRow label={t('ns.voice.release')} sub={t('ns.voice.releaseSub')}>
              <Range
                min={0}
                max={1000}
                step={50}
                value={prefs.pttReleaseMs}
                aria-label={t('ns.voice.release')}
                onChange={(e) => setPrefs({ pttReleaseMs: Number(e.target.value) })}
              />
              <span className="nx-num ns-range-v">
                {t('set.voice.releaseMs', { n: prefs.pttReleaseMs })}
              </span>
            </SetRow>
          </>
        ) : (
          <SetRow label={t('ns.voice.sensitivity')} sub={t('ns.voice.sensitivitySub')}>
            <Range
              min={0.005}
              max={0.4}
              step={0.005}
              value={prefs.sensitivity}
              aria-label={t('ns.voice.sensitivity')}
              onChange={(e) => setPrefs({ sensitivity: Number(e.target.value) })}
            />
          </SetRow>
        )}
        <Meter />
      </SetGroup>
      <NoiseSuppressionGroup />
      <KeybindsGroup />
      <CameraGroup cameras={list.filter((d) => d.kind === 'videoinput')} />
      {/* the icon hangs, the text wraps under itself (T15) */}
      <p className="nx-set-after">
        <Icon name="info" />
        <span>{t('ns.voice.processing')}</span>
      </p>
    </>
  );
}
